export const AYZEO_INSIGHTS_TIME_RANGES = ["24h", "7d", "14d", "30d", "60d", "90d", "6m", "1y", "all"] as const;
export type AyzeoInsightsTimeRange = typeof AYZEO_INSIGHTS_TIME_RANGES[number];
export type AyzeoInsightsModel = "ai_overview" | "chatgpt";

export interface AyzeoHistoricalPromptInsights {
  promptSnapshotId: string;
  externalPromptId: string;
  promptWording: string;
  model: AyzeoInsightsModel;
  modelLabel: string;
  requestedTimeRange?: AyzeoInsightsTimeRange;
  requestedStartDate?: string;
  requestedEndDate?: string;
  startDate: string;
  endDate: string;
  retrievedAt: string;
  answerCount: number;
  ownBrandMentions: number;
  mentionRatePercent: number;
  answerTextAvailable?: number;
  answerTextTotal?: number;
  brandDetectionAvailable?: number;
  brandDetectionTotal?: number;
  queryCaptureAvailable?: number;
  queryCaptureTotal?: number;
  recurringBrands: Array<{
    name: string;
    answerCount?: number;
    sharePercent?: number;
    averagePosition?: number;
    positionAnswerCount?: number;
    isOwnBrand?: boolean;
    modelCounts?: Record<string, number>;
  }>;
}

type JsonRecord = Record<string, unknown>;
const asRecord = (value: unknown): JsonRecord => value && typeof value === "object" && !Array.isArray(value) ? value as JsonRecord : {};
const asText = (value: unknown): string | undefined => typeof value === "string" && value.trim() ? value.trim() : undefined;
const asNumber = (value: unknown): number | undefined => typeof value === "number" && Number.isFinite(value) ? value : undefined;
const first = (record: JsonRecord, keys: string[]) => keys.map(key => record[key]).find(value => value !== undefined && value !== null);
const firstText = (record: JsonRecord, keys: string[]) => asText(first(record, keys));
const firstNumber = (record: JsonRecord, keys: string[]) => asNumber(first(record, keys));
const validUtcDate = (value: unknown): string | undefined => {
  const text = asText(value);
  if (!text || !/^\d{4}-\d{2}-\d{2}$/u.test(text)) return undefined;
  const date = new Date(`${text}T00:00:00Z`);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== text) return undefined;
  return text;
};

export function validateAyzeoInsightsRequest(input: unknown): { model: AyzeoInsightsModel; timeRange?: AyzeoInsightsTimeRange; startDate?: string; endDate?: string } {
  const value = asRecord(input);
  const model = value.model;
  if (model !== "ai_overview" && model !== "chatgpt") throw new Error("Choose a supported Ayzeo model.");
  if (value.timeRange !== undefined) {
    if (!AYZEO_INSIGHTS_TIME_RANGES.includes(value.timeRange as AyzeoInsightsTimeRange)) throw new Error("Choose a supported historical period.");
    if (value.startDate !== undefined || value.endDate !== undefined) throw new Error("Choose either a preset period or exact UTC dates.");
    return { model, timeRange: value.timeRange as AyzeoInsightsTimeRange };
  }
  const startDate = validUtcDate(value.startDate), endDate = validUtcDate(value.endDate);
  if (!startDate || !endDate || startDate > endDate) throw new Error("Enter a valid UTC start date and end date.");
  return { model, startDate, endDate };
}

function unwrap(value: unknown): unknown {
  const record = asRecord(value);
  if (record.structuredContent && typeof record.structuredContent === "object") return record.structuredContent;
  const items = Array.isArray(record.content) ? record.content : [];
  for (const item of items) {
    const text = asText(asRecord(item).text);
    if (!text) continue;
    try { return JSON.parse(text); } catch { /* Try the next MCP content item. */ }
  }
  return value;
}

function records(value: unknown, output: JsonRecord[] = []): JsonRecord[] {
  if (Array.isArray(value)) for (const item of value) records(item, output);
  else if (value && typeof value === "object") {
    output.push(value as JsonRecord);
    for (const child of Object.values(value as JsonRecord)) records(child, output);
  }
  return output;
}

function findRecord(value: unknown, keys: string[]): JsonRecord | undefined {
  return records(value).find(item => keys.some(key => item[key] !== undefined && item[key] !== null));
}

function coverageCount(value: unknown, availableNames: string[], totalNames: string[]): { available?: number; total?: number } {
  const row = asRecord(value);
  const available = firstNumber(row, availableNames);
  const total = firstNumber(row, totalNames);
  if (available !== undefined && total !== undefined) return { available, total };
  const text = typeof value === "string" ? value.match(/(\d+)\s*(?:of|\/)\s*(\d+)/u) : null;
  return text ? { available: Number(text[1]), total: Number(text[2]) } : {};
}

/** Reduce the MCP result to historical summary data; answer text and archive pages never cross to the browser. */
export function normalizeAyzeoHistoricalPromptInsights(
  raw: unknown,
  context: Pick<AyzeoHistoricalPromptInsights, "promptSnapshotId" | "externalPromptId" | "promptWording" | "model" | "modelLabel" | "retrievedAt" | "requestedTimeRange" | "requestedStartDate" | "requestedEndDate">,
): AyzeoHistoricalPromptInsights {
  const result = unwrap(raw), root = asRecord(result);
  const period = asRecord(root.scope).startDate !== undefined || asRecord(root.scope).endDate !== undefined
    ? asRecord(root.scope)
    : findRecord(result, ["startDate", "periodStart", "from"]);
  const startDate = validUtcDate(first(period ?? {}, ["startDate", "periodStart", "from", "start"]));
  const endDate = validUtcDate(first(period ?? {}, ["endDate", "periodEnd", "to", "end"]));
  const summary = Object.keys(asRecord(root.summary)).length > 0
    ? asRecord(root.summary)
    : findRecord(result, ["ownBrandMentions", "ownBrandMentionCount", "mentionRate", "mentionRatePercent", "totalAnswers", "answerCount"]);
  const answerCount = firstNumber(summary ?? {}, ["runs", "totalAnswers", "answerCount", "storedAnswers", "successfulRuns", "totalRuns"]);
  const ownBrandMentions = firstNumber(summary ?? {}, ["ownBrandMentions", "ownBrandMentionCount", "brandMentions", "mentionCount"]);
  const rawRate = first(summary ?? {}, ["mentionRatePercent", "ownBrandMentionRate", "ownBrandMentionRatePercent", "mentionRatePercentage", "mentionRate"]);
  const rateRecord = asRecord(rawRate);
  const mentionRatePercent = asNumber(rawRate) ?? firstNumber(rateRecord, ["percent", "percentage", "value"]);
  if (!startDate) throw new Error("Ayzeo historical insights are missing a valid returned start date.");
  if (!endDate) throw new Error("Ayzeo historical insights are missing a valid returned end date.");
  if (startDate > endDate) throw new Error("Ayzeo historical insights returned an invalid UTC period.");
  if (answerCount === undefined) throw new Error("Ayzeo historical insights are missing the total run count.");
  if (ownBrandMentions === undefined) throw new Error("Ayzeo historical insights are missing the own-brand mention count.");
  if (mentionRatePercent === undefined) throw new Error("Ayzeo historical insights are missing the own-brand mention rate.");

  const coverage = summary ?? findRecord(result, ["answerText", "answerTextCoverage", "textCoverage", "brandDetection", "brandDetectionCoverage", "brandCoverage", "answersWithText", "answersWithBrandDetection"]);
  const explicitScalarCoverage = (key: string) => {
    const value = firstNumber(coverage ?? {}, [key]);
    return value === undefined ? undefined : { available: value, total: answerCount };
  };
  const textCoverage = explicitScalarCoverage("answersWithText")
    ?? coverageCount(first(coverage ?? {}, ["answerText", "answerTextCoverage", "textCoverage"]), ["available", "count", "captured"], ["total", "denominator", "totalAnswers"]);
  const brandCoverage = explicitScalarCoverage("answersWithBrandDetection")
    ?? coverageCount(first(coverage ?? {}, ["brandDetection", "brandDetectionCoverage", "brandCoverage"]), ["available", "count", "captured"], ["total", "denominator", "totalAnswers"]);
  const queryCoverage = explicitScalarCoverage("answersWithQueryCapture");
  const recurringContainer = asRecord(root.recurringBrands);
  const brandsValue = Array.isArray(root.recurringBrands) ? root.recurringBrands
    : Array.isArray(recurringContainer.rows) ? recurringContainer.rows
      : first(asRecord(result), ["brands"])
        ?? first(findRecord(result, ["recurringBrands"]) ?? {}, ["recurringBrands", "brands"]);
  const recurringBrands = (Array.isArray(brandsValue) ? brandsValue : []).flatMap(item => {
    const brand = asRecord(item), name = firstText(brand, ["name", "brand", "brandName"]);
    if (!name) return [];
    const position = asRecord(first(brand, ["averagePosition", "avgPosition", "position"]));
    const rawShare = firstNumber(brand, ["shareOfAnswersWithBrandDetection", "sharePercent", "share", "percentage"]);
    const rate = rawShare !== undefined && brand.shareOfAnswersWithBrandDetection === undefined && brand.sharePercent === undefined && rawShare >= 0 && rawShare <= 1 ? rawShare * 100 : rawShare;
    const modelCounts = Object.fromEntries(Object.entries(asRecord(brand.models)).flatMap(([model, count]) => {
      const safeCount = asNumber(count);
      return safeCount === undefined ? [] : [[model, safeCount] as const];
    }));
    return [{
      name,
      answerCount: firstNumber(brand, ["answerCount", "answers", "count"]),
      sharePercent: rate,
      averagePosition: asNumber(first(brand, ["averagePosition", "avgMentionPosition", "avgPosition"])) ?? firstNumber(position, ["average", "mean", "value"]),
      positionAnswerCount: firstNumber(brand, ["positionSamples", "positionAnswerCount", "rankedAnswers", "positionCount"]) ?? firstNumber(position, ["answerCount", "count"]),
      isOwnBrand: typeof first(brand, ["isOwnBrand", "ownBrand", "isYou"]) === "boolean" ? first(brand, ["isOwnBrand", "ownBrand", "isYou"]) as boolean : undefined,
      modelCounts: Object.keys(modelCounts).length > 0 ? modelCounts : undefined,
    }];
  });

  return {
    ...context,
    startDate,
    endDate,
    answerCount,
    ownBrandMentions,
    mentionRatePercent,
    answerTextAvailable: textCoverage.available,
    answerTextTotal: textCoverage.total,
    brandDetectionAvailable: brandCoverage.available,
    brandDetectionTotal: brandCoverage.total,
    queryCaptureAvailable: queryCoverage?.available,
    queryCaptureTotal: queryCoverage?.total,
    recurringBrands,
  };
}

export function ayzeoInsightsToolArguments(
  inputSchema: unknown,
  request: ReturnType<typeof validateAyzeoInsightsRequest> & { promptId: string },
): Record<string, unknown> {
  const properties = asRecord(asRecord(inputSchema).properties);
  const arguments_: Record<string, unknown> = {};
  const setRequired = (names: string[], value: unknown, expected: string) => {
    const key = names.find(name => name in properties);
    if (!key) throw new Error(`Ayzeo historical insights tool schema is missing the required '${expected}' property.`);
    arguments_[key] = value;
  };
  setRequired(["promptId", "prompt_id"], request.promptId, "promptId");
  setRequired(["model"], request.model, "model");
  if (request.timeRange) {
    setRequired(["timeRange", "time_range"], request.timeRange, "timeRange");
  } else {
    setRequired(["startDate", "start_date"], request.startDate, "startDate");
    setRequired(["endDate", "end_date"], request.endDate, "endDate");
  }
  return arguments_;
}
